import { resolveWorkoutDeliveryFormatKey } from '~/utils/trainingDeliveryFormat';
import { isWorkoutCalendarDayKnown } from '~/utils/workoutScheduleContext';
import { utcFromLocalParts } from '~/utils/timezone';

export const TRAINER_PARTICIPATION_FORMATS = ['gym_together', 'video_together'];

export const DEFAULT_TRAINER_WORKOUT_DURATION_MIN = 60;

function resolveScalarValue(raw) {
  if (raw && typeof raw === 'object' && 'val' in raw) {
    return raw.val;
  }
  return raw;
}

function resolveTrainingDateYmd(workout) {
  const fromDateSort = String(workout?.date_sort ?? '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromDateSort)) {
    return fromDateSort;
  }

  const scheduleContext = workout?.schedule_context;
  const fromEffectiveDate = String(scheduleContext?.effective_date ?? '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromEffectiveDate)) {
    return fromEffectiveDate;
  }

  const fromTrainingDate = String(resolveScalarValue(workout?.training_date) ?? '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromTrainingDate)) {
    return fromTrainingDate;
  }

  return '';
}

function resolveStartTimeRaw(workout) {
  const fromStartTime = String(resolveScalarValue(workout?.start_time) ?? '').trim();
  if (fromStartTime && fromStartTime !== '--:--') {
    return fromStartTime;
  }

  const fromEffective = String(workout?.schedule_context?.effective_start_time ?? '').trim();
  if (fromEffective && fromEffective !== '--:--') {
    return fromEffective;
  }

  const scheduleTime = String(workout?.schedule?.time ?? '').trim();
  if (scheduleTime && scheduleTime !== '--:--') {
    return scheduleTime;
  }

  return '';
}

/**
 * @param {string} timeRaw
 * @returns {number|null}
 */
export function parseScheduleTimeToMinutes(timeRaw) {
  const normalized = String(timeRaw ?? '').trim();
  if (!normalized || normalized === '--:--') {
    return null;
  }

  const match = normalized.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

/**
 * @param {Record<string, unknown>|null|undefined} workout
 * @param {{ durationMin?: number }} [options]
 * @returns {{ dateYmd: string, startMin: number, endMin: number }|null}
 */
export function resolveWorkoutScheduleInterval(workout, options = {}) {
  if (!workout || typeof workout !== 'object') {
    return null;
  }

  const scheduleContext = workout.schedule_context;
  if (scheduleContext && !isWorkoutCalendarDayKnown(scheduleContext)) {
    return null;
  }

  const scheduledAtUtc = String(scheduleContext?.scheduled_at_utc ?? workout?.scheduled_at_utc ?? '').trim();
  if (scheduledAtUtc) {
    const startTs = Date.parse(scheduledAtUtc.includes('T') ? scheduledAtUtc : `${scheduledAtUtc.replace(' ', 'T')}Z`);
    if (!Number.isNaN(startTs)) {
      const durationMin = Math.max(
        1,
        Number(options.durationMin ?? DEFAULT_TRAINER_WORKOUT_DURATION_MIN) || DEFAULT_TRAINER_WORKOUT_DURATION_MIN,
      );
      return {
        startTs,
        endTs: startTs + durationMin * 60 * 1000,
      };
    }
  }

  const dateYmd = resolveTrainingDateYmd(workout);
  if (!dateYmd) {
    return null;
  }

  const startMin = parseScheduleTimeToMinutes(resolveStartTimeRaw(workout));
  if (startMin === null) {
    return null;
  }

  const scheduleTimezone = String(scheduleContext?.schedule_timezone ?? 'Europe/Moscow').trim();
  const utcRaw = utcFromLocalParts(dateYmd, resolveStartTimeRaw(workout), scheduleTimezone);
  if (utcRaw) {
    const startTs = Date.parse(`${utcRaw.replace(' ', 'T')}Z`);
    const durationMin = Math.max(
      1,
      Number(options.durationMin ?? DEFAULT_TRAINER_WORKOUT_DURATION_MIN) || DEFAULT_TRAINER_WORKOUT_DURATION_MIN,
    );
    if (!Number.isNaN(startTs)) {
      return {
        startTs,
        endTs: startTs + durationMin * 60 * 1000,
      };
    }
  }

  const durationMin = Math.max(
    1,
    Number(options.durationMin ?? DEFAULT_TRAINER_WORKOUT_DURATION_MIN) || DEFAULT_TRAINER_WORKOUT_DURATION_MIN,
  );

  return {
    dateYmd,
    startMin,
    endMin: startMin + durationMin,
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} workout
 */
export function isTrainerParticipationWorkout(workout) {
  const formatKey = resolveWorkoutDeliveryFormatKey(workout);
  return TRAINER_PARTICIPATION_FORMATS.includes(formatKey);
}

/**
 * @param {Record<string, unknown>|null|undefined} workout
 */
export function resolveWorkoutScheduleOverlapKey(workout) {
  return String(workout?.uuid ?? workout?.id ?? '').trim();
}

function intervalsOverlap(left, right) {
  if (typeof left.startTs === 'number' && typeof right.startTs === 'number') {
    return left.startTs < right.endTs && right.startTs < left.endTs;
  }
  return left.startMin < right.endMin && right.startMin < left.endMin;
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} workouts
 * @param {{ durationMin?: number }} [options]
 * @returns {Map<string, Array<{ key: string, workout: Record<string, unknown> }>>}
 */
export function detectWorkoutScheduleOverlaps(workouts, options = {}) {
  /** @type {Array<{ key: string, workout: Record<string, unknown>, interval: Record<string, number> }>} */
  const entries = [];

  for (const workout of workouts || []) {
    if (!isTrainerParticipationWorkout(workout)) {
      continue;
    }

    const key = resolveWorkoutScheduleOverlapKey(workout);
    if (!key) {
      continue;
    }

    const interval = resolveWorkoutScheduleInterval(workout, options);
    if (!interval) {
      continue;
    }

    entries.push({ key, workout, interval });
  }

  /** @type {Map<string, Array<{ key: string, workout: Record<string, unknown> }>>} */
  const overlaps = new Map();

  for (let leftIndex = 0; leftIndex < entries.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < entries.length; rightIndex += 1) {
      const left = entries[leftIndex];
      const right = entries[rightIndex];

      if (!intervalsOverlap(left.interval, right.interval)) {
        continue;
      }

      if (!overlaps.has(left.key)) {
        overlaps.set(left.key, []);
      }
      if (!overlaps.has(right.key)) {
        overlaps.set(right.key, []);
      }

      overlaps.get(left.key).push({ key: right.key, workout: right.workout });
      overlaps.get(right.key).push({ key: left.key, workout: left.workout });
    }
  }

  return overlaps;
}

/**
 * @param {Map<string, Array<{ key: string, workout: Record<string, unknown> }>>} overlaps
 * @returns {Set<string>}
 */
export function collectScheduleOverlapWorkoutIds(overlaps) {
  return new Set(overlaps?.keys?.() ?? []);
}

/**
 * @param {Map<string, Array<{ key: string, workout: Record<string, unknown> }>>} overlaps
 * @param {string} workoutKey
 */
export function formatScheduleOverlapTooltip(overlaps, workoutKey) {
  const entries = overlaps?.get?.(workoutKey) ?? [];
  if (!entries.length) {
    return '';
  }

  const labels = entries.map((entry) => {
    const title = String(entry.workout?.title ?? 'Тренировка').trim() || 'Тренировка';
    const personName = String(entry.workout?.person_name ?? '').trim();
    return personName ? `«${title}» (${personName})` : `«${title}»`;
  });

  if (labels.length === 1) {
    return `Пересекается по времени с ${labels[0]}`;
  }

  return `Пересекается по времени с: ${labels.join('; ')}`;
}

/**
 * @param {Map<string, Array<{ key: string, workout: Record<string, unknown> }>>} overlaps
 */
export function formatScheduleOverlapBanner(overlaps) {
  const count = collectScheduleOverlapWorkoutIds(overlaps).size;
  if (count <= 0) {
    return '';
  }

  const mod10 = count % 10;
  const mod100 = count % 100;
  let word = 'тренировок';

  if (mod10 === 1 && mod100 !== 11) {
    word = 'тренировка';
  } else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    word = 'тренировки';
  }

  return `${count} ${word} с вашим участием пересекаются по времени`;
}
